import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, Radio, RefreshCw, Search, Trash2 } from 'lucide-react';
import {
  clearWorkspaceQueryLogs,
  listWorkspaceQueryLogs,
  openWorkspaceQueryLogLive,
  type WorkspaceQueryLog,
} from '../queryLogsApi';
import { logDataModels } from '../dataModelsLogger';
import { formatRelative } from '../dataModelsUtils';
import { DialogShell, ghostBtn, iconBtn, primaryBtn, searchClass, surfaces } from './dataModelsUi';
import QueryLogsMetricsDialog from './QueryLogsMetricsDialog';

const KIND_LABEL: Record<string, string> = {
  preview: 'Preview',
  query: 'Trino query',
  governed: 'Governed query',
  file: 'File query',
};

const INNER_TABS = [
  { id: 'logs', label: 'Logs' },
  { id: 'live', label: 'Live queries' },
] as const;

type LogsInnerTab = (typeof INNER_TABS)[number]['id'];

const ENABLE_LOG_PREFIX = 'selficai.data-models.query-log-enabled';

function enableLogKey(workspaceId: string, catalogId?: string, assetId?: string): string {
  const tenantId = typeof window === 'undefined' ? '' : window.localStorage.getItem('tenant_id')?.trim() || 'anon';
  return `${ENABLE_LOG_PREFIX}:${tenantId}:${workspaceId}:${catalogId || '-'}:${assetId || '-'}`;
}

function readEnableLog(workspaceId: string, catalogId?: string, assetId?: string): boolean {
  if (typeof window === 'undefined' || !workspaceId) return false;
  const key = enableLogKey(workspaceId, catalogId, assetId);
  try {
    if (window.localStorage.getItem(key) === '1') return true;
    if (catalogId || assetId) return false;
    const tenantId = window.localStorage.getItem('tenant_id')?.trim() || 'anon';
    const legacy =
      window.sessionStorage.getItem(key) === '1' ||
      window.sessionStorage.getItem(`${ENABLE_LOG_PREFIX}:${workspaceId}`) === '1' ||
      window.localStorage.getItem(`${ENABLE_LOG_PREFIX}:${tenantId}:${workspaceId}`) === '1';
    if (legacy) {
      window.localStorage.setItem(key, '1');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function writeEnableLog(workspaceId: string, enabled: boolean, catalogId?: string, assetId?: string): void {
  if (typeof window === 'undefined' || !workspaceId) return;
  const key = enableLogKey(workspaceId, catalogId, assetId);
  try {
    if (enabled) window.localStorage.setItem(key, '1');
    else {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
      window.sessionStorage.removeItem(`${ENABLE_LOG_PREFIX}:${workspaceId}`);
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unable to persist Enable log';
    logDataModels('warning', 'workspace_query_logs_enable_persist_failed', { workspaceId, catalogId, assetId, message });
  }
}

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] || kind || 'Query';
}

function outcomeLabel(outcome: string): string {
  if (outcome === 'denied') return 'Denied';
  if (outcome === 'failed') return 'Failed';
  return 'Succeeded';
}

function detailText(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(', ') || '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function prependEvent(current: WorkspaceQueryLog[], event: WorkspaceQueryLog, cap?: number): WorkspaceQueryLog[] {
  const next = [event, ...current.filter((item) => item.eventId !== event.eventId)];
  return cap ? next.slice(0, cap) : next;
}

function dateInputClass(isLight: boolean): string {
  return `${searchClass(isLight).replace('pl-8', 'px-2')} w-[13.75rem]`;
}

function toRangeIso(from: string, to: string): { from?: string; to?: string } {
  let start = from;
  let end = to;
  if (start && end && start > end) {
    [start, end] = [end, start];
  }
  return {
    from: start ? localDateTimeToIso(start, 'start') : undefined,
    to: end ? localDateTimeToIso(end, 'end') : undefined,
  };
}

function localDateTimeToIso(value: string, bound: 'start' | 'end'): string {
  const raw = value.trim();
  let local = raw.includes('T') ? raw : `${raw}T${bound === 'start' ? '00:00:00' : '23:59:59'}`;
  if (bound === 'end' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) local = `${local}:59`;
  const stamp = new Date(local);
  return Number.isNaN(stamp.getTime()) ? raw : stamp.toISOString();
}

function parseOccurredAt(iso: string): Date | null {
  const raw = (iso || '').trim();
  if (!raw) return null;
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const normalized = hasZone ? raw : `${raw.replace(' ', 'T')}Z`;
  const stamp = new Date(normalized);
  return Number.isNaN(stamp.getTime()) ? null : stamp;
}

function currentTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function formatDateTime(iso: string): string {
  const stamp = parseOccurredAt(iso);
  if (!stamp) return '—';
  return stamp.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: currentTimeZone(),
    timeZoneName: 'short',
  });
}

function formatOccurredRelative(iso: string): string {
  const stamp = parseOccurredAt(iso);
  return stamp ? formatRelative(stamp.toISOString()) : '—';
}

function WhenStamp({ iso, muted }: { iso: string; muted: string }) {
  return (
    <span className="block">
      <span className="block whitespace-nowrap">{formatDateTime(iso)}</span>
      <span className={`block text-[11px] ${muted}`}>{formatOccurredRelative(iso)}</span>
    </span>
  );
}

function eventInRange(event: WorkspaceQueryLog, fromIso?: string, toIso?: string): boolean {
  if (!fromIso && !toIso) return true;
  const stamp = parseOccurredAt(event.occurredAt);
  if (!stamp) return false;
  const at = stamp.getTime();
  if (fromIso && at < Date.parse(fromIso)) return false;
  if (toIso && at > Date.parse(toIso)) return false;
  return true;
}

export default function QueryLogsPanel({
  isLight,
  workspaceId,
  catalogId,
  assetId,
}: {
  isLight: boolean;
  workspaceId: string;
  catalogId?: string;
  assetId?: string;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const scope = useMemo(
    () => ({
      catalogId: catalogId?.trim() || undefined,
      assetId: assetId?.trim() || undefined,
    }),
    [assetId, catalogId],
  );
  const scopeKey = `${workspaceId}:${scope.catalogId || ''}:${scope.assetId || ''}`;
  const scopeLabel = scope.assetId ? 'this asset' : scope.catalogId ? 'this data model' : 'this WorkSpace';
  const [innerTab, setInnerTab] = useState<LogsInnerTab>('logs');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [metricsOpen, setMetricsOpen] = useState(false);
  const range = useMemo(() => toRangeIso(from, to), [from, to]);
  const metricsQuery = useMemo(
    () => ({
      workspaceId,
      catalogId: scope.catalogId,
      assetId: scope.assetId,
      q: query.trim() || undefined,
      from: range.from,
      to: range.to,
    }),
    [query, range.from, range.to, scope.assetId, scope.catalogId, workspaceId],
  );
  const [items, setItems] = useState<WorkspaceQueryLog[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<WorkspaceQueryLog | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [logEnabled, setLogEnabled] = useState(() => readEnableLog(workspaceId, scope.catalogId, scope.assetId));
  const [logError, setLogError] = useState('');
  const [liveOn, setLiveOn] = useState(false);
  const [liveError, setLiveError] = useState('');
  const [liveEvents, setLiveEvents] = useState<WorkspaceQueryLog[]>([]);
  const logAbort = useRef<AbortController | null>(null);
  const liveAbort = useRef<AbortController | null>(null);

  const load = useCallback(
    async (nextCursor?: string) => {
      if (!workspaceId) return;
      setLoading(true);
      setError('');
      try {
        const page = await listWorkspaceQueryLogs({
          workspaceId,
          catalogId: scope.catalogId,
          assetId: scope.assetId,
          q: query.trim() || undefined,
          from: range.from,
          to: range.to,
          cursor: nextCursor,
          limit: 50,
        });
        setItems((current) => (nextCursor ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load query logs');
      } finally {
        setLoading(false);
      }
    },
    [query, range.from, range.to, scope.assetId, scope.catalogId, workspaceId],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(handle);
  }, [load]);

  useEffect(() => {
    setInnerTab('logs');
    setQuery('');
    setFrom('');
    setTo('');
    setMetricsOpen(false);
    setSelected(null);
    setConfirmClear(false);
    setLogEnabled(readEnableLog(workspaceId, scope.catalogId, scope.assetId));
    setLogError('');
    setLiveError('');
    setLiveEvents([]);
    return () => {
      logAbort.current?.abort();
      logAbort.current = null;
      liveAbort.current?.abort();
      liveAbort.current = null;
    };
  }, [scope.assetId, scope.catalogId, workspaceId]);

  const stopLogCapture = useCallback(() => {
    if (!logAbort.current) return;
    logAbort.current.abort();
    logAbort.current = null;
    logDataModels('app', 'workspace_query_logs_enable_stop', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId });
  }, [scope.assetId, scope.catalogId, workspaceId]);

  const startLogCapture = useCallback(() => {
    logAbort.current?.abort();
    const controller = new AbortController();
    logAbort.current = controller;
    setLogError('');
    logDataModels('app', 'workspace_query_logs_enable_start', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId });
    void openWorkspaceQueryLogLive(
      workspaceId,
      (event) => {
        if (!eventInRange(event, range.from, range.to)) return;
        setItems((current) => prependEvent(current, event));
      },
      controller.signal,
      scope,
    ).catch((err) => {
      if (controller.signal.aborted) return;
      setLogError(err instanceof Error ? err.message : 'Log capture stopped');
    });
  }, [range.from, range.to, scope, workspaceId]);

  useEffect(() => {
    if (!logEnabled) {
      stopLogCapture();
      return undefined;
    }
    startLogCapture();
    return () => stopLogCapture();
  }, [logEnabled, startLogCapture, stopLogCapture, scopeKey]);

  const stopLive = useCallback(() => {
    if (!liveAbort.current && !liveOn) return;
    liveAbort.current?.abort();
    liveAbort.current = null;
    setLiveOn(false);
    logDataModels('app', 'workspace_query_logs_live_ui_stop', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId });
  }, [liveOn, scope.assetId, scope.catalogId, workspaceId]);

  const startLive = useCallback(() => {
    liveAbort.current?.abort();
    const controller = new AbortController();
    liveAbort.current = controller;
    setLiveOn(true);
    setLiveError('');
    setLiveEvents([]);
    logDataModels('app', 'workspace_query_logs_live_ui_start', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId });
    void openWorkspaceQueryLogLive(
      workspaceId,
      (event) => {
        setLiveEvents((current) => prependEvent(current, event, 50));
      },
      controller.signal,
      scope,
    ).catch((err) => {
      if (controller.signal.aborted) return;
      setLiveError(err instanceof Error ? err.message : 'Live debug stopped');
      setLiveOn(false);
    });
  }, [scope, workspaceId]);

  useEffect(() => {
    if (innerTab !== 'live') stopLive();
  }, [innerTab, stopLive]);

  const onInnerTab = useCallback(
    (next: LogsInnerTab) => {
      if (next === innerTab) return;
      setInnerTab(next);
      logDataModels('app', 'workspace_query_logs_inner_tab', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId, tab: next });
    },
    [innerTab, scope.assetId, scope.catalogId, workspaceId],
  );

  const onToggleLog = useCallback(() => {
    const next = !logEnabled;
    setLogEnabled(next);
    writeEnableLog(workspaceId, next, scope.catalogId, scope.assetId);
    logDataModels('app', 'workspace_query_logs_enable_toggled', {
      workspaceId,
      catalogId: scope.catalogId,
      assetId: scope.assetId,
      enabled: next,
    });
  }, [logEnabled, scope.assetId, scope.catalogId, workspaceId]);

  const onRefresh = useCallback(() => {
    logDataModels('app', 'workspace_query_logs_refresh', { workspaceId, catalogId: scope.catalogId, assetId: scope.assetId, logEnabled });
    void load();
    if (logEnabled) startLogCapture();
  }, [load, logEnabled, scope.assetId, scope.catalogId, startLogCapture, workspaceId]);

  const onClear = useCallback(async () => {
    setClearing(true);
    setError('');
    try {
      await clearWorkspaceQueryLogs(workspaceId, scope);
      setItems([]);
      setCursor(null);
      setSelected(null);
      setConfirmClear(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to clear query logs');
    } finally {
      setClearing(false);
    }
  }, [scope, workspaceId]);

  const detailRows = useMemo(() => {
    if (!selected) return [];
    const detail = selected.detail || {};
    const rows: Array<{ label: string; value: string; pre?: boolean }> = [
      { label: 'When', value: formatDateTime(selected.occurredAt) },
      { label: 'Who', value: [selected.actorName || '—', selected.actorEmail].filter(Boolean).join(' · ') },
      { label: 'API', value: `${selected.method} ${selected.api}` },
      { label: 'Kind', value: kindLabel(selected.kind) },
      { label: 'Outcome', value: `${outcomeLabel(selected.outcome)}${selected.statusCode ? ` (${selected.statusCode})` : ''}` },
      { label: 'Asset', value: selected.assetName || selected.assetId || '—' },
      { label: 'Catalog', value: selected.catalogName || selected.catalogId || '—' },
      { label: 'Duration', value: `${selected.durationMs} ms` },
      { label: 'Rows', value: selected.rowCount == null ? '—' : String(selected.rowCount) },
    ];
    if (detail.sql) rows.push({ label: 'SQL', value: detailText(detail.sql), pre: true });
    if (detail.opaAllow != null) rows.push({ label: 'OPA', value: detail.opaAllow ? 'Allow' : 'Deny' });
    if (detail.opaFilters != null) rows.push({ label: 'Row filters', value: detailText(detail.opaFilters) });
    if (detail.opaMasks != null) rows.push({ label: 'Mask columns', value: detailText(detail.opaMasks) });
    if (detail.appliedMasks != null) rows.push({ label: 'Applied masks', value: detailText(detail.appliedMasks), pre: true });
    if (detail.error) rows.push({ label: 'Error', value: detailText(detail.error) });
    return rows;
  }, [selected]);

  return (
    <div className="space-y-4">
      <div className={`flex gap-5 border-b ${ui.border}`}>
        {INNER_TABS.map((item) => {
          const active = innerTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onInnerTab(item.id)}
              className={`relative py-2 text-sm ${active ? `font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}` : muted}`}
            >
              {item.label}
              {active ? <span className={`absolute inset-x-0 -bottom-px h-0.5 ${isLight ? 'bg-[#1C1C1C]' : 'bg-white'}`} /> : null}
            </button>
          );
        })}
      </div>

      {innerTab === 'logs' ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search API, asset, or actor" className={searchClass(isLight)} />
          </div>
          <input
            type="datetime-local"
            value={from}
            max={to || undefined}
            step={60}
            onChange={(event) => setFrom(event.target.value)}
            title="From date and time"
            aria-label="From date and time"
            className={dateInputClass(isLight)}
          />
          <span className={`text-[12px] ${muted}`}>to</span>
          <input
            type="datetime-local"
            value={to}
            min={from || undefined}
            step={60}
            onChange={(event) => setTo(event.target.value)}
            title="To date and time"
            aria-label="To date and time"
            className={dateInputClass(isLight)}
          />
          <button
            type="button"
            className={iconBtn(isLight)}
            title="Query metrics"
            aria-label="Query metrics"
            onClick={() => setMetricsOpen(true)}
          >
            <BarChart3 size={14} />
          </button>
          <button
            type="button"
            className={iconBtn(isLight)}
            title="Refresh logs"
            aria-label="Refresh logs"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} />
          </button>
          <button type="button" className={ghostBtn(isLight)} onClick={onToggleLog} aria-pressed={logEnabled}>
            <span
              className={`relative inline-flex h-4 w-7 items-center rounded-full ${
                logEnabled ? 'bg-[#1B6CA8]' : isLight ? 'bg-[#D0D5DD]' : 'bg-white/20'
              }`}
              aria-hidden
            >
              <span
                className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
                  logEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </span>
            Enable log
          </button>
          <button type="button" className={ghostBtn(isLight)} onClick={() => setConfirmClear(true)} disabled={!items.length || clearing}>
            <Trash2 size={14} />
            Clear
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {liveOn ? (
            <button type="button" className={ghostBtn(isLight)} onClick={stopLive}>
              <Radio size={14} />
              Stop live
            </button>
          ) : (
            <button type="button" className={ghostBtn(isLight)} onClick={startLive}>
              <Radio size={14} />
              Start live
            </button>
          )}
        </div>
      )}

      {innerTab === 'logs' && logEnabled ? (
        <p className={`text-[12px] ${muted}`}>Logging incoming query hits for {scopeLabel}.</p>
      ) : null}

      {innerTab === 'logs' && error ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{error}</p> : null}
      {innerTab === 'logs' && logError ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{logError}</p> : null}
      {innerTab === 'live' && liveError ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{liveError}</p> : null}

      {innerTab === 'logs' ? (
        <>
          {loading && !items.length ? <p className={`py-8 text-sm ${muted}`}>Loading logs…</p> : null}
          {!loading && !items.length ? (
            <p className={`py-8 text-sm ${muted}`}>
              {from || to ? `No query hits for ${scopeLabel} in this time range.` : `No query hits for ${scopeLabel} yet.`}
            </p>
          ) : null}
          {items.length ? <QueryLogTable isLight={isLight} items={items} onSelect={setSelected} /> : null}
          {cursor ? (
            <button type="button" className={ghostBtn(isLight)} disabled={loading} onClick={() => void load(cursor)}>
              Load more
            </button>
          ) : null}
        </>
      ) : (
        <section className={`rounded-md border px-3 py-2.5 ${ui.card}`}>
          <div className="mb-2 flex items-center justify-between">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${muted}`}>Live debug</p>
            <span className={`text-[11px] ${muted}`}>
              {liveOn
                ? liveEvents.length
                  ? `${liveEvents.length} event${liveEvents.length === 1 ? '' : 's'}`
                  : 'Waiting for hits…'
                : 'Start live to stream query hits'}
            </span>
          </div>
          {liveEvents.length ? (
            <div className="max-h-[420px] space-y-1 overflow-y-auto text-[13px]">
              {liveEvents.map((event) => (
                <button
                  key={`live-${event.eventId}`}
                  type="button"
                  className={`flex w-full items-center gap-3 rounded px-1 py-1 text-left ${ui.row}`}
                  onClick={() => setSelected(event)}
                >
                  <span className={`w-16 shrink-0 ${muted}`}>{kindLabel(event.kind)}</span>
                  <span className="min-w-0 flex-1 truncate">{event.summary}</span>
                  <span className={`shrink-0 text-right ${muted}`}>
                    <WhenStamp iso={event.occurredAt} muted={muted} />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className={`text-sm ${muted}`}>
              {liveOn ? `Streaming query hits for ${scopeLabel}.` : 'Live queries are not running. Start live to watch hits in real time.'}
            </p>
          )}
        </section>
      )}

      {metricsOpen ? (
        <QueryLogsMetricsDialog isLight={isLight} scopeLabel={scopeLabel} query={metricsQuery} onClose={() => setMetricsOpen(false)} />
      ) : null}

      {selected ? (
        <DialogShell
          title="Query detail"
          subtitle={selected.summary}
          isLight={isLight}
          size="medium"
          onClose={() => setSelected(null)}
          footer={
            <button type="button" className={primaryBtn()} onClick={() => setSelected(null)}>
              Close
            </button>
          }
        >
          <dl className="grid grid-cols-[140px_minmax(0,1fr)] gap-y-2 text-sm">
            {detailRows.map((row) => (
              <React.Fragment key={row.label}>
                <dt className={muted}>{row.label}</dt>
                <dd className={row.pre ? 'whitespace-pre-wrap break-all font-mono text-[12px]' : 'break-words'}>{row.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </DialogShell>
      ) : null}

      {confirmClear ? (
        <DialogShell
          title="Clear query logs"
          subtitle={`This removes stored query hits for ${scopeLabel}. It cannot be undone.`}
          isLight={isLight}
          onClose={() => setConfirmClear(false)}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} onClick={() => setConfirmClear(false)} disabled={clearing}>
                Cancel
              </button>
              <button type="button" className={primaryBtn()} onClick={() => void onClear()} disabled={clearing}>
                Clear logs
              </button>
            </>
          }
        >
          <p className={`text-sm ${muted}`}>
            {logEnabled ? 'Enable log will keep adding new hits after you clear history.' : 'Turn on Enable log to keep saving new hits after you clear history.'}
          </p>
        </DialogShell>
      ) : null}
    </div>
  );
}

function QueryLogTable({
  isLight,
  items,
  onSelect,
}: {
  isLight: boolean;
  items: WorkspaceQueryLog[];
  onSelect: (event: WorkspaceQueryLog) => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  return (
    <div className={`overflow-hidden rounded-md border ${ui.card}`}>
      <table className="min-w-full text-[13px]">
        <thead className={ui.tableHead}>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
            <th className="px-3 py-2.5">When</th>
            <th className="px-3 py-2.5">Who</th>
            <th className="px-3 py-2.5">API</th>
            <th className="px-3 py-2.5">Asset</th>
            <th className="px-3 py-2.5">Outcome</th>
            <th className="px-3 py-2.5">Summary</th>
          </tr>
        </thead>
        <tbody>
          {items.map((event) => (
            <tr
              key={event.eventId}
              className={`cursor-pointer border-t ${ui.border} ${ui.row}`}
              onClick={() => onSelect(event)}
            >
              <td className={`px-3 py-2.5 ${muted}`}>
                <WhenStamp iso={event.occurredAt} muted={muted} />
              </td>
              <td className="px-3 py-2.5">
                <span className={`block ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{event.actorName || event.actorEmail || '—'}</span>
                {event.actorEmail ? <span className={`block text-[11px] ${muted}`}>{event.actorEmail}</span> : null}
              </td>
              <td className={`whitespace-nowrap px-3 py-2.5 ${muted}`}>{event.api}</td>
              <td className={`max-w-[180px] truncate px-3 py-2.5 ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
                {event.assetName || event.assetId || '—'}
              </td>
              <td className={`whitespace-nowrap px-3 py-2.5 ${muted}`}>{outcomeLabel(event.outcome)}</td>
              <td className={`max-w-[420px] truncate px-3 py-2.5 ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{event.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
